import { useState, useEffect, useCallback, useRef } from "react";

// ── PWA Setup ─────────────────────────────────────────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// ── Supabase ──────────────────────────────────────────────────────────────────
const SB_URL = "https://lqnfkimcoqzlbegyuxnj.supabase.co";
const SB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxxbmZraW1jb3F6bGJlZ3l1eG5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODUxNDI3MDEsImV4cCI6MjEwMDcxODcwMX0.aPeKRH_D6v8JDpzCaaji8GIeV2s06qxfIwdj-OJDndE";

const getToken = () => localStorage.getItem("wf_token") || SB_KEY;

const sb = async (path, method = "GET", body = null) => {
  const res = await fetch(`${SB_URL}/rest/v1/${path}`, {
    method,
    headers: {
      apikey: SB_KEY,
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: body ? JSON.stringify(body) : null,
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text);
  return text ? JSON.parse(text) : [];
};

// Auth API
const sbAuth = async (endpoint, body) => {
  const res = await fetch(`${SB_URL}/auth/v1/${endpoint}`, {
    method: "POST",
    headers: { apikey: SB_KEY, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error_description || data.msg || "Auth error");
  return data;
};

// ── Constants ─────────────────────────────────────────────────────────────────
const C = { accent:"#6C63FF", success:"#22C55E", warning:"#F59E0B", danger:"#EF4444", purple:"#A78BFA", bg:"#0D0F1A", surface:"#161929", card:"#1C2035", border:"#252A40", muted:"#6B7280", text:"#F0F0F8" };
const CL = { accent:"#6C63FF", success:"#22C55E", warning:"#F59E0B", danger:"#EF4444", purple:"#A78BFA", bg:"#F4F5FB", surface:"#FFFFFF", card:"#ECEEF8", border:"#D1D5DB", muted:"#6B7280", text:"#111827" };

const STATUS_FLOW = ["Not Started","Started","In Progress","Sent","Completed"];
const STATUS_COLOR = { "Not Started":"#6B7280","Started":"#38BDF8","In Progress":"#F59E0B","Sent":"#A78BFA","Completed":"#22C55E" };
const CATEGORIES = ["Reel","Logo","Poster","Website","Visiting Card","Video Edit","Photo Edit","Content","Other"];
const TAGS = ["Urgent","VIP","Monthly","One Time","Repeat Client","High Priority","Design","Marketing"];
const PRIORITIES = ["Low","Medium","High"];
const PRIORITY_COLOR = { Low:"#22C55E", Medium:"#F59E0B", High:"#EF4444" };
const PRIORITY_STARS = { Low:1, Medium:2, High:3 };

const WA_TEMPLATES = {
  "Started": (title,client) => `Hi ${client}, aapka *${title}* kaam shuru ho gaya hai. Jald hi update milega. 🚀`,
  "In Progress": (title,client) => `Hi ${client}, *${title}* par kaam chal raha hai. Progress update aata rahega. ✅`,
  "Sent": (title,client) => `Hi ${client}, *${title}* aapko bhej diya gaya hai. Please check karein aur feedback dein. 📤`,
  "Completed": (title,client) => `Hi ${client}, *${title}* complete ho gaya hai! Delivery ready hai. Payment confirm karein. 🎉`,
  "Revision": (title,client) => `Hi ${client}, *${title}* mein revision ki gaya hai. Updated version jald milega. 🔄`,
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}) : "";
const todayStr = () => new Date().toDateString();
const isOverdue = (dl,st) => dl && st!=="Completed" && new Date(dl)<new Date();
const monthName = () => new Date().toLocaleDateString("en-IN",{month:"long",year:"numeric"});

function useBreakpoint() {
  const [w,setW] = useState(window.innerWidth);
  useEffect(()=>{ const fn=()=>setW(window.innerWidth); window.addEventListener("resize",fn); return ()=>window.removeEventListener("resize",fn); },[]);
  return { isMobile:w<768, isTablet:w>=768&&w<1024, isDesktop:w>=1024, w };
}

// ── LOGIN SCREEN ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email,setEmail] = useState("");
  const [pass,setPass] = useState("");
  const [mode,setMode] = useState("login"); // login | signup
  const [loading,setLoading] = useState(false);
  const [err,setErr] = useState("");

  const handle = async () => {
    if (!email||!pass) { setErr("Please fill all fields"); return; }
    setLoading(true); setErr("");
    try {
      const data = await sbAuth(mode==="login"?"token?grant_type=password":"signup", {email,password:pass});
      if (data.access_token) {
        localStorage.setItem("wf_token", data.access_token);
        localStorage.setItem("wf_user", JSON.stringify(data.user));
        onLogin(data.user);
      } else if (mode==="signup") {
        setErr("Check your email to confirm signup, then login.");
        setMode("login");
      }
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"'Inter',system-ui,sans-serif"}}>
      <div style={{width:"100%",maxWidth:380}}>
        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:56,height:56,background:C.accent,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{color:C.text,fontWeight:900,fontSize:26,letterSpacing:"-0.5px"}}>Workflow</div>
          <div style={{color:C.muted,fontSize:13,marginTop:4}}>Freelancer Project Management</div>
        </div>

        <div style={{background:C.surface,borderRadius:16,padding:28,border:`1px solid ${C.border}`}}>
          <div style={{color:C.text,fontWeight:800,fontSize:18,marginBottom:20}}>{mode==="login"?"Welcome back":"Create account"}</div>

          <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
            <input value={email} onChange={e=>setEmail(e.target.value)}
              style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,color:C.text,padding:"12px 14px",fontSize:14,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"}}
              placeholder="Email address" type="email"/>
            <input value={pass} onChange={e=>setPass(e.target.value)}
              style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,color:C.text,padding:"12px 14px",fontSize:14,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"}}
              placeholder="Password" type="password"
              onKeyDown={e=>e.key==="Enter"&&handle()}/>
          </div>

          {err && <div style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"10px 14px",color:C.danger,fontSize:13,marginBottom:16}}>{err}</div>}

          <button onClick={handle} disabled={loading}
            style={{width:"100%",background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"13px",fontWeight:700,fontSize:15,cursor:"pointer",opacity:loading?0.7:1}}>
            {loading?"Please wait…":mode==="login"?"Sign In →":"Create Account →"}
          </button>

          <div style={{textAlign:"center",marginTop:16,color:C.muted,fontSize:13}}>
            {mode==="login"?"Don't have an account? ":"Already have an account? "}
            <button onClick={()=>{setMode(m=>m==="login"?"signup":"login");setErr("");}}
              style={{background:"none",border:"none",color:C.accent,fontWeight:700,cursor:"pointer",fontSize:13}}>
              {mode==="login"?"Sign Up":"Sign In"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [dark,setDark] = useState(true);
  const T = dark ? C : CL;
  const bp = useBreakpoint();

  const [clients,setClients] = useState([]);
  const [works,setWorks] = useState([]);
  const [loading,setLoading] = useState(true);
  const [view,setView] = useState("dashboard");
  const [activeClient,setActiveClient] = useState(null);
  const [activeWork,setActiveWork] = useState(null);
  const [searchQ,setSearchQ] = useState("");
  const [showSearch,setShowSearch] = useState(false);
  const [toast,setToast] = useState(null);
  const [modal,setModal] = useState(null);
  const [showQuickAdd,setShowQuickAdd] = useState(false);
  const [analyticsMonth,setAnalyticsMonth] = useState(new Date().getMonth());
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Auth check
  useEffect(()=>{
    const token = localStorage.getItem("wf_token");
    const savedUser = localStorage.getItem("wf_user");
    if (token && savedUser) { setUser(JSON.parse(savedUser)); }
    setAuthChecked(true);
  },[]);

  const notify = (msg,type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2800); };

  // Request notification permission
  useEffect(()=>{ if("Notification" in window) Notification.requestPermission(); },[]);

  const loadAll = useCallback(async()=>{
    if (!user) return;
    try {
      setLoading(true);
      const [cls,wks,revs,progs,tls] = await Promise.all([
        sb("clients?order=created_at.desc"),
        sb("works?order=created_at.desc"),
        sb("revisions?order=created_at.asc"),
        sb("progress?order=created_at.asc"),
        sb("timeline?order=created_at.asc"),
      ]);
      setClients(cls);
      setWorks(wks.map(w=>({...w,tags:w.tags||[],revisions:revs.filter(r=>r.work_id===w.id),progress:progs.filter(p=>p.work_id===w.id),timeline:tls.filter(t=>t.work_id===w.id)})));
    } catch(e){ notify("Load error","error"); } finally { setLoading(false); }
  },[user]);

  useEffect(()=>{ if(user) loadAll(); },[loadAll,user]);

  // Check overdue deadlines and notify
  useEffect(()=>{
    if (!works.length) return;
    const overdue = works.filter(w=>isOverdue(w.deadline,w.status));
    overdue.forEach(w=>{
      if ("Notification" in window && Notification.permission==="granted") {
        const key = `notified_${w.id}_${w.deadline}`;
        if (!localStorage.getItem(key)) {
          new Notification("⚠️ Workflow — Overdue Work", { body: `"${w.title}" deadline crossed!`, icon: "/favicon.ico" });
          localStorage.setItem(key,"1");
        }
      }
    });
  },[works]);

  const syncWork = u => { setWorks(p=>p.map(w=>w.id===u.id?u:w)); if(activeWork?.id===u.id) setActiveWork(u); };

  const addClient = async d => { const [c]=await sb("clients","POST",{...d,user_id:user.id}); setClients(p=>[c,...p]); notify("Client added! 👤"); };
  const addWork = async d => {
    const [w]=await sb("works","POST",{...d,budget:+d.budget||0,advance:+d.advance||0,user_id:user.id});
    await sb("timeline","POST",{work_id:w.id,event:"Project Created",user_id:user.id});
    setWorks(p=>[{...w,tags:w.tags||[],revisions:[],progress:[],timeline:[{event:"Project Created",created_at:new Date().toISOString()}]},...p]);
    notify("Work created! 🎯");
  };
  const advanceStatus = async w => {
    const idx=STATUS_FLOW.indexOf(w.status); if(idx>=STATUS_FLOW.length-1) return;
    const next=STATUS_FLOW[idx+1];
    await sb(`works?id=eq.${w.id}`,"PATCH",{status:next});
    await sb("timeline","POST",{work_id:w.id,event:`Moved to ${next}`,user_id:user.id});
    syncWork({...w,status:next,timeline:[...w.timeline,{event:`Moved to ${next}`,created_at:new Date().toISOString()}]});
    notify(`→ ${next}`);
  };
  const addRevision = async (w,note) => {
    const [rev]=await sb("revisions","POST",{work_id:w.id,note,user_id:user.id});
    const num=w.revisions.length+1;
    await sb("timeline","POST",{work_id:w.id,event:`Revision ${num} Added`,user_id:user.id});
    syncWork({...w,revisions:[...w.revisions,rev],timeline:[...w.timeline,{event:`Revision ${num} Added`,created_at:new Date().toISOString()}]});
    notify(`Revision ${num} added!`);
  };
  const addProgress = async (w,line) => { const [p]=await sb("progress","POST",{work_id:w.id,line,user_id:user.id}); syncWork({...w,progress:[...w.progress,p]}); notify("Progress saved!"); };
  const toggleDelivered = async w => {
    const val=!w.delivered;
    await sb(`works?id=eq.${w.id}`,"PATCH",{delivered:val});
    let tl=w.timeline;
    if(val){await sb("timeline","POST",{work_id:w.id,event:"Delivered",user_id:user.id}); tl=[...tl,{event:"Delivered",created_at:new Date().toISOString()}];}
    syncWork({...w,delivered:val,timeline:tl}); notify(val?"✓ Delivered!":"Undone");
  };
  const setPayment = async (w,state) => {
    const advance_received=state==="advance"||state==="full", paid=state==="full";
    await sb(`works?id=eq.${w.id}`,"PATCH",{advance_received,paid});
    let tl=w.timeline;
    if(state==="advance"&&!w.advance_received){await sb("timeline","POST",{work_id:w.id,event:"Advance Payment Received",user_id:user.id}); tl=[...tl,{event:"Advance Payment Received",created_at:new Date().toISOString()}];}
    if(state==="full"&&!w.paid){await sb("timeline","POST",{work_id:w.id,event:"Full Payment Received",user_id:user.id}); tl=[...tl,{event:"Full Payment Received",created_at:new Date().toISOString()}];}
    syncWork({...w,advance_received,paid,timeline:tl}); notify(state==="full"?"💰 Full Payment Done!":state==="advance"?"Advance received!":"Reset");
  };
  const saveNotes = async (w,notes) => { await sb(`works?id=eq.${w.id}`,"PATCH",{notes}); syncWork({...w,notes}); };

  // WhatsApp send
  const sendWhatsApp = (work, type="status") => {
    const cl = clients.find(c=>c.id===work.client_id);
    const msg = WA_TEMPLATES[type==="revision"?"Revision":work.status]?.(work.title, cl?.name||"Client") || `Hi, update on *${work.title}*`;
    const phone = cl?.phone?.replace(/\D/g,"") || "";
    window.open(`https://wa.me/${phone.startsWith("91")?phone:"91"+phone}?text=${encodeURIComponent(msg)}`,"_blank");
  };

  // Analytics
  const getMonthlyStats = (monthIdx) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), monthIdx, 1);
    const end = new Date(now.getFullYear(), monthIdx+1, 0);
    const monthWorks = works.filter(w=>{
      const d = new Date(w.created_at);
      return d>=start && d<=end;
    });
    return {
      total: monthWorks.length,
      completed: monthWorks.filter(w=>w.status==="Completed").length,
      revenue: monthWorks.reduce((a,w)=>a+(w.budget||0),0),
      received: monthWorks.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0),
      revisions: monthWorks.reduce((a,w)=>a+w.revisions.length,0),
      clients: [...new Set(monthWorks.map(w=>w.client_id))].length,
    };
  };

  // Client health score
  const getClientHealth = (cid) => {
    const cw = works.filter(w=>w.client_id===cid);
    if (!cw.length) return "new";
    const paidRatio = cw.filter(w=>w.paid).length / cw.length;
    const revAvg = cw.reduce((a,w)=>a+w.revisions.length,0) / cw.length;
    const overdueCount = cw.filter(w=>isOverdue(w.deadline,w.status)).length;
    if (paidRatio>0.7 && revAvg<2 && overdueCount===0) return "vip";
    if (paidRatio<0.3 || overdueCount>0 || revAvg>4) return "problem";
    return "normal";
  };

  // Computed
  const todayWorks=works.filter(w=>new Date(w.created_at).toDateString()===todayStr());
  const pendingWorks=works.filter(w=>w.status!=="Completed");
  const completedWorks=works.filter(w=>w.status==="Completed");
  const overdueWorks=works.filter(w=>isOverdue(w.deadline,w.status));
  const todayRevisions=works.filter(w=>w.revisions.some(r=>new Date(r.created_at).toDateString()===todayStr()));
  const unpaidWorks=works.filter(w=>!w.paid);
  const clientWorks=cid=>works.filter(w=>w.client_id===cid);
  const totalBudget=works.reduce((a,w)=>a+(w.budget||0),0);
  const totalReceived=works.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0);

  const searchResults=searchQ.length>1?{
    clients:clients.filter(c=>`${c.name} ${c.company||""}`.toLowerCase().includes(searchQ.toLowerCase())),
    works:works.filter(w=>`${w.title} ${w.category} ${w.status}`.toLowerCase().includes(searchQ.toLowerCase())),
  }:null;

  const openClient=c=>{setActiveClient(c);setView("client-detail");};
  const openWork=w=>{setActiveWork(w);setView("work-detail");};
  const goBack=()=>{ if(view==="work-detail"){setView("client-detail");setActiveWork(null);} else{setView("clients");setActiveClient(null);} };
  const navTo=v=>{setView(v);setSearchQ("");setShowSearch(false);};

  const logout = () => { localStorage.removeItem("wf_token"); localStorage.removeItem("wf_user"); setUser(null); setClients([]); setWorks([]); };

  if (!authChecked) return <Loader/>;
  if (!user) return <LoginScreen onLogin={u=>setUser(u)}/>;
  if (loading) return <Loader/>;

  const isClientOrWork = view==="client-detail"||view==="work-detail";
  const monthStats = getMonthlyStats(analyticsMonth);
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Inter','Segoe UI',system-ui,sans-serif",color:T.text,paddingBottom:bp.isMobile?70:0}}>
      <style>{`*{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}input,textarea,select{-webkit-appearance:none;}button{cursor:pointer;font-family:inherit;}::-webkit-scrollbar{width:3px;}::-webkit-scrollbar-thumb{background:${T.border};border-radius:4px;}`}</style>
      {toast&&<Toast msg={toast.msg} type={toast.type}/>}

      {/* HEADER */}
      <header style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:bp.isMobile?"12px 16px":"14px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {isClientOrWork&&<button onClick={goBack} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:8,padding:"5px 12px",fontSize:13,fontWeight:600}}>← Back</button>}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:30,height:30,background:C.accent,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <span style={{fontWeight:800,fontSize:bp.isMobile?15:17,color:T.text,letterSpacing:"-0.3px"}}>Workflow</span>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {!bp.isMobile&&(
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:13}}>🔍</span>
              <input value={searchQ} onChange={e=>{setSearchQ(e.target.value);if(e.target.value)setView("search");else setView("dashboard");}}
                style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"8px 12px 8px 32px",fontSize:13,outline:"none",width:220}}
                placeholder="Search clients, work…"/>
            </div>
          )}
          {bp.isMobile&&<button onClick={()=>setShowSearch(s=>!s)} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",fontSize:15}}>🔍</button>}
          <button onClick={()=>setDark(d=>!d)} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",fontSize:15}}>{dark?"☀️":"🌙"}</button>
          <div style={{position:"relative"}}>
            <button onClick={()=>setModal(modal==="user-menu"?null:"user-menu")}
              style={{width:32,height:32,borderRadius:"50%",background:C.accent,border:"none",color:"#fff",fontWeight:800,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {user?.email?.[0]?.toUpperCase()||"W"}
            </button>
            {modal==="user-menu"&&(
              <div style={{position:"absolute",right:0,top:40,background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:8,minWidth:200,zIndex:200,boxShadow:"0 8px 24px #0006"}}>
                <div style={{padding:"8px 12px",color:T.muted,fontSize:12,borderBottom:`1px solid ${T.border}`,marginBottom:4}}>{user?.email}</div>
                <button onClick={()=>{setModal(null);navTo("analytics");}} style={{width:"100%",background:"none",border:"none",color:T.text,padding:"9px 12px",fontSize:13,textAlign:"left",borderRadius:8}}>📊 Analytics</button>
                <button onClick={()=>{setModal(null);logout();}} style={{width:"100%",background:"none",border:"none",color:C.danger,padding:"9px 12px",fontSize:13,textAlign:"left",borderRadius:8}}>🚪 Sign Out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {bp.isMobile&&showSearch&&(
        <div style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:"10px 14px"}}>
          <input value={searchQ} onChange={e=>{setSearchQ(e.target.value);if(e.target.value)setView("search");else setView("dashboard");}} autoFocus
            style={{background:T.input||T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"10px 14px",fontSize:14,outline:"none",width:"100%"}}
            placeholder="Search…"/>
        </div>
      )}

      {/* DESKTOP NAV */}
      {!bp.isMobile&&(
        <nav style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:"0 24px",display:"flex",gap:4}}>
          {[["dashboard","📊 Dashboard"],["clients","👥 Clients"],["analytics","📈 Analytics"]].map(([k,l])=>(
            <button key={k} onClick={()=>navTo(k)}
              style={{background:"none",border:"none",color:(view===k||view==="client-detail"&&k==="clients"||view==="work-detail"&&k==="clients")?C.accent:T.muted,padding:"13px 18px",fontSize:14,fontWeight:700,borderBottom:`3px solid ${(view===k||view==="client-detail"&&k==="clients"||view==="work-detail"&&k==="clients")?C.accent:"transparent"}`,transition:"all 0.15s"}}>
              {l}
            </button>
          ))}
        </nav>
      )}

      <main style={{padding:bp.isMobile?"14px":"24px 24px",maxWidth:"100%"}}>

        {/* SEARCH */}
        {view==="search"&&(
          <div>
            <h2 style={{color:T.text,fontWeight:800,fontSize:20,marginBottom:16}}>"{searchQ}"</h2>
            {searchResults?.clients.map(c=>(
              <div key={c.id} onClick={()=>openClient(c)} style={{background:T.surface,borderRadius:12,padding:"14px 16px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:12,border:`1px solid ${T.border}`}}>
                <Av name={c.name} size={38}/><div><b style={{color:T.text}}>{c.name}</b><div style={{color:T.muted,fontSize:12}}>{c.company}</div></div>
              </div>
            ))}
            {searchResults?.works.map(w=>(
              <div key={w.id} onClick={()=>openWork(w)} style={{background:T.surface,borderRadius:12,padding:"14px 16px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:12,border:`1px solid ${T.border}`}}>
                <StatusDot status={w.status}/><div style={{flex:1}}><b style={{color:T.text}}>{w.title}</b><div style={{color:T.muted,fontSize:12}}>{w.category}</div></div><SBadge status={w.status}/>
              </div>
            ))}
            {!searchResults?.clients.length&&!searchResults?.works.length&&<Empty icon="🔍" msg="No results found"/>}
          </div>
        )}

        {/* DASHBOARD */}
        {view==="dashboard"&&(
          <div>
            {overdueWorks.length>0&&(
              <div style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:12,padding:"12px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:18}}>🚨</span>
                  <div>
                    <div style={{color:C.danger,fontWeight:700,fontSize:13}}>Overdue Alert — {overdueWorks.length} work{overdueWorks.length>1?"s":""}</div>
                    <div style={{color:T.muted,fontSize:12}}>{overdueWorks.map(w=>w.title).join(", ")}</div>
                  </div>
                </div>
                <button style={{background:C.danger,color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700}}>RESOLVE</button>
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"repeat(2,1fr)":"repeat(6,1fr)",gap:10,marginBottom:14}}>
              {[
                {label:"TODAY'S WORK",value:todayWorks.length,sub:`${works.filter(w=>w.priority==="High").length} high priority`,color:C.accent},
                {label:"PENDING",value:pendingWorks.length,sub:"Awaiting client",color:C.warning},
                {label:"COMPLETED",value:completedWorks.length,sub:"This week",color:C.success},
                {label:"REVISIONS",value:todayRevisions.length,sub:"Needs attention",color:C.purple},
                {label:"PAYMENTS",value:unpaidWorks.length,sub:"Not yet paid",color:"#38BDF8"},
                {label:"OVERDUE",value:overdueWorks.length,sub:overdueWorks.length>0?"Critical":"All good",color:C.danger},
              ].map(({label,value,sub,color})=>(
                <div key={label} style={{background:T.surface,borderRadius:12,padding:bp.isMobile?"12px 10px":"14px",border:`1px solid ${T.border}`,position:"relative",overflow:"hidden"}}>
                  <div style={{color:T.muted,fontSize:9,fontWeight:700,letterSpacing:"0.08em",marginBottom:6}}>{label}</div>
                  <div style={{color,fontSize:bp.isMobile?24:28,fontWeight:900,lineHeight:1,marginBottom:3}}>{value}</div>
                  <div style={{color:T.muted,fontSize:9}}>{sub}</div>
                  <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:color}}/>
                </div>
              ))}
            </div>

            {/* Revenue */}
            <div style={{background:T.surface,borderRadius:14,padding:"16px 18px",border:`1px solid ${T.border}`,marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div><div style={{fontWeight:700,fontSize:14}}>Revenue</div><div style={{color:T.muted,fontSize:12}}>{monthName()}</div></div>
                <div style={{color:C.accent,fontWeight:900,fontSize:20}}>₹{totalBudget.toLocaleString()}</div>
              </div>
              <div style={{background:T.card,borderRadius:4,height:8,marginBottom:12,overflow:"hidden"}}>
                <div style={{background:`linear-gradient(90deg,${C.accent},${C.success})`,height:"100%",width:`${totalBudget>0?Math.min(100,totalReceived/totalBudget*100):0}%`,borderRadius:4,transition:"width 0.5s"}}/>
              </div>
              <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:8,height:8,borderRadius:"50%",background:C.accent}}/><span style={{color:T.muted,fontSize:12}}>Received</span><span style={{color:T.text,fontWeight:700,fontSize:13,marginLeft:4}}>₹{totalReceived.toLocaleString()}</span></div>
                <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:8,height:8,borderRadius:"50%",background:C.warning}}/><span style={{color:T.muted,fontSize:12}}>Pending</span><span style={{color:T.text,fontWeight:700,fontSize:13,marginLeft:4}}>₹{(totalBudget-totalReceived).toLocaleString()}</span></div>
              </div>
            </div>

            {/* Today + Activity + Payments */}
            {todayWorks.length>0&&(
              <div style={{background:T.surface,borderRadius:14,padding:"16px 18px",border:`1px solid ${T.border}`,marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontWeight:700,fontSize:14}}>Today's Work</div>
                  <button onClick={()=>navTo("clients")} style={{background:"none",border:"none",color:C.accent,fontSize:12,fontWeight:700}}>VIEW ALL</button>
                </div>
                {todayWorks.slice(0,4).map(w=>{
                  const cl=clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id} onClick={()=>openWork(w)} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 0",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}>
                      <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${w.status==="Completed"?C.success:T.border}`,background:w.status==="Completed"?C.success:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {w.status==="Completed"&&<span style={{color:"#fff",fontSize:9}}>✓</span>}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{color:T.text,fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
                        <div style={{color:T.muted,fontSize:11}}>Client: {cl?.name||"—"}</div>
                      </div>
                      <SBadge status={w.status}/>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"1fr":"1fr 1fr",gap:14}}>
              <div style={{background:T.surface,borderRadius:14,padding:"16px 18px",border:`1px solid ${T.border}`}}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:14}}>Recent Activity</div>
                {works.length===0&&<Empty icon="⚡" msg="No activity yet"/>}
                {works.slice(0,5).flatMap(w=>w.timeline.slice(-1).map((t,i)=>{
                  const cl=clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id+i} style={{display:"flex",gap:10,padding:"9px 0",borderBottom:`1px solid ${T.border}`,alignItems:"flex-start"}}>
                      <Av name={cl?.name||"W"} size={28}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{color:T.text,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title} — {t.event}</div>
                        <div style={{color:T.muted,fontSize:11}}>{fmt(t.created_at)}</div>
                      </div>
                    </div>
                  );
                })).slice(0,6)}
              </div>
              <div style={{background:T.surface,borderRadius:14,padding:"16px 18px",border:`1px solid ${T.border}`}}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:14}}>Pending Payments</div>
                {unpaidWorks.length===0&&<Empty icon="🎉" msg="All cleared!"/>}
                {unpaidWorks.slice(0,5).map(w=>{
                  const cl=clients.find(c=>c.id===w.client_id);
                  return (
                    <div key={w.id} onClick={()=>openWork(w)} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"9px 0",borderBottom:`1px solid ${T.border}`,cursor:"pointer",gap:8}}>
                      <div style={{minWidth:0}}>
                        <div style={{color:T.text,fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
                        <div style={{color:T.muted,fontSize:11}}>{cl?.name}{w.deadline?` · Due ${fmtDate(w.deadline)}`:""}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{color:C.danger,fontWeight:800,fontSize:13}}>₹{((w.budget||0)-(w.advance||0)).toLocaleString()}</div>
                        {isOverdue(w.deadline,w.status)&&<div style={{color:C.danger,fontSize:10,fontWeight:700}}>OVERDUE</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ANALYTICS */}
        {view==="analytics"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
              <h2 style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?18:22,margin:0}}>📈 Analytics</h2>
              <select value={analyticsMonth} onChange={e=>setAnalyticsMonth(+e.target.value)}
                style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"8px 14px",fontSize:13,outline:"none"}}>
                {MONTHS.map((m,i)=><option key={i} value={i}>{m} {new Date().getFullYear()}</option>)}
              </select>
            </div>

            {/* Monthly Stats */}
            <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"repeat(2,1fr)":"repeat(3,1fr)",gap:12,marginBottom:20}}>
              {[
                {label:"Total Works",value:monthStats.total,color:C.accent,icon:"📋"},
                {label:"Completed",value:monthStats.completed,color:C.success,icon:"✅"},
                {label:"Revenue",value:`₹${monthStats.revenue.toLocaleString()}`,color:C.accent,icon:"💰"},
                {label:"Received",value:`₹${monthStats.received.toLocaleString()}`,color:C.success,icon:"💳"},
                {label:"Total Revisions",value:monthStats.revisions,color:C.purple,icon:"🔄"},
                {label:"Active Clients",value:monthStats.clients,color:"#38BDF8",icon:"👥"},
              ].map(({label,value,color,icon})=>(
                <div key={label} style={{background:T.surface,borderRadius:14,padding:"18px",border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:24,marginBottom:6}}>{icon}</div>
                  <div style={{color,fontWeight:900,fontSize:24,marginBottom:4}}>{value}</div>
                  <div style={{color:T.muted,fontSize:12}}>{label}</div>
                </div>
              ))}
            </div>

            {/* Client Health */}
            <div style={{background:T.surface,borderRadius:14,padding:"18px",border:`1px solid ${T.border}`,marginBottom:16}}>
              <div style={{fontWeight:700,fontSize:14,marginBottom:14}}>Client Health Score</div>
              {clients.length===0&&<Empty icon="👥" msg="No clients yet"/>}
              {clients.map(c=>{
                const health=getClientHealth(c.id);
                const cw=clientWorks(c.id);
                const healthConfig={ vip:{color:C.success,label:"VIP 🟢",bg:"rgba(34,197,94,0.1)"}, normal:{color:C.warning,label:"Normal 🟡",bg:"rgba(245,158,11,0.1)"}, problem:{color:C.danger,label:"Problem 🔴",bg:"rgba(239,68,68,0.1)"}, new:{color:T.muted,label:"New ⚪",bg:T.card} };
                const h=healthConfig[health];
                return (
                  <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${T.border}`,gap:10}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <Av name={c.name} size={36}/>
                      <div>
                        <div style={{color:T.text,fontWeight:600,fontSize:13}}>{c.name}</div>
                        <div style={{color:T.muted,fontSize:11}}>{cw.length} works · {cw.filter(w=>w.paid).length} paid</div>
                      </div>
                    </div>
                    <div style={{background:h.bg,color:h.color,borderRadius:20,padding:"4px 12px",fontSize:11,fontWeight:700}}>{h.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Best vs Worst */}
            {clients.length>0&&(
              <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"1fr":"1fr 1fr",gap:14}}>
                <div style={{background:T.surface,borderRadius:14,padding:"18px",border:`1px solid ${T.border}`}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14,color:C.success}}>🏆 Best Clients</div>
                  {clients.filter(c=>getClientHealth(c.id)==="vip").slice(0,3).map(c=>(
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
                      <Av name={c.name} size={30}/>
                      <div style={{color:T.text,fontSize:13,fontWeight:600}}>{c.name}</div>
                      <div style={{marginLeft:"auto",color:C.success,fontSize:11,fontWeight:700}}>₹{clientWorks(c.id).reduce((a,w)=>a+(w.budget||0),0).toLocaleString()}</div>
                    </div>
                  ))}
                  {clients.filter(c=>getClientHealth(c.id)==="vip").length===0&&<div style={{color:T.muted,fontSize:13}}>Complete more works to see VIP clients</div>}
                </div>
                <div style={{background:T.surface,borderRadius:14,padding:"18px",border:`1px solid ${T.border}`}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14,color:C.danger}}>⚠️ Needs Attention</div>
                  {clients.filter(c=>getClientHealth(c.id)==="problem").slice(0,3).map(c=>(
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
                      <Av name={c.name} size={30}/>
                      <div style={{color:T.text,fontSize:13,fontWeight:600}}>{c.name}</div>
                      <div style={{marginLeft:"auto",color:C.danger,fontSize:11,fontWeight:700}}>{clientWorks(c.id).filter(w=>!w.paid).length} unpaid</div>
                    </div>
                  ))}
                  {clients.filter(c=>getClientHealth(c.id)==="problem").length===0&&<div style={{color:T.muted,fontSize:13}}>All clients are in good standing!</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CLIENTS */}
        {view==="clients"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
              <div>
                <h2 style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?18:22,margin:0}}>Clients</h2>
                <div style={{color:T.muted,fontSize:12,marginTop:2}}>Manage your active network</div>
              </div>
              <button onClick={()=>setModal("add-client")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"9px 18px",fontWeight:700,fontSize:13}}>+ New Client</button>
            </div>
            <div style={{position:"relative",marginBottom:14}}>
              <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:T.muted}}>🔍</span>
              <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search clients…"
                style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"10px 14px 10px 36px",fontSize:13,outline:"none",width:"100%"}}/>
            </div>
            {clients.filter(c=>`${c.name} ${c.company||""}`.toLowerCase().includes(searchQ.toLowerCase())).map(c=>{
              const cw=clientWorks(c.id);
              const health=getClientHealth(c.id);
              const healthBadge={vip:{color:C.success,bg:"rgba(34,197,94,0.1)",label:"VIP"},normal:{color:C.warning,bg:"rgba(245,158,11,0.1)",label:"Active"},problem:{color:C.danger,bg:"rgba(239,68,68,0.1)",label:"Attention"},new:{color:T.muted,bg:T.card,label:"New"}};
              const hb=healthBadge[health];
              return (
                <div key={c.id} onClick={()=>openClient(c)} style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"12px 14px":"14px 18px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:14,border:`1px solid ${T.border}`}}>
                  <Av name={c.name} size={bp.isMobile?40:46}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                      <div style={{color:T.text,fontWeight:700,fontSize:bp.isMobile?14:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
                      <div style={{background:hb.bg,color:hb.color,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700,flexShrink:0}}>{hb.label}</div>
                    </div>
                    {c.company&&<div style={{color:T.muted,fontSize:12}}>{c.company}</div>}
                    {c.phone&&<div style={{color:T.muted,fontSize:12}}>📱 {c.phone}</div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{color:C.accent,fontWeight:800,fontSize:16}}>{cw.length}</div>
                    <div style={{color:T.muted,fontSize:11}}>works</div>
                    {cw.filter(w=>!w.paid).length>0&&<div style={{color:C.danger,fontSize:11,fontWeight:700}}>₹{cw.filter(w=>!w.paid).reduce((a,w)=>a+((w.budget||0)-(w.advance||0)),0).toLocaleString()} due</div>}
                  </div>
                </div>
              );
            })}
            {clients.length===0&&<Empty icon="👥" msg="No clients yet. Add your first!"/>}
          </div>
        )}

        {/* CLIENT DETAIL */}
        {view==="client-detail"&&activeClient&&(
          <div>
            <div style={{background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",marginBottom:16,border:`1px solid ${T.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,flexWrap:"wrap",gap:8}}>
                <div style={{display:"flex",gap:14,alignItems:"center"}}>
                  <Av name={activeClient.name} size={bp.isMobile?44:52}/>
                  <div>
                    <div style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?16:19}}>{activeClient.name}</div>
                    {activeClient.company&&<div style={{background:C.accent+"22",color:C.accent,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600,display:"inline-block",marginTop:4}}>{activeClient.company}</div>}
                  </div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {activeClient.phone&&(
                    <button onClick={()=>window.open(`https://wa.me/91${activeClient.phone.replace(/\D/,"")}?text=${encodeURIComponent(`Hi ${activeClient.name}, Workflow se update bhej raha hoon.`)}`,"_blank")}
                      style={{background:"#25D366",color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontWeight:700,fontSize:12}}>📤 WhatsApp</button>
                  )}
                  <button onClick={()=>setModal("report")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontWeight:700,fontSize:12}}>📄 Report</button>
                </div>
              </div>
              {activeClient.phone&&<div style={{color:T.muted,fontSize:13,marginBottom:4}}>📱 {activeClient.phone}</div>}
              {activeClient.email&&<div style={{color:T.muted,fontSize:13,marginBottom:4}}>✉️ {activeClient.email}</div>}
              {activeClient.notes&&<div style={{color:C.warning,fontSize:13,marginTop:8,background:C.warning+"11",borderRadius:8,padding:"8px 12px"}}>📝 {activeClient.notes}</div>}
            </div>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{color:T.text,fontWeight:700,fontSize:14}}>Works ({clientWorks(activeClient.id).length})</div>
              <button onClick={()=>setModal("add-work")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:12}}>+ Add Work</button>
            </div>

            {clientWorks(activeClient.id).length===0&&<Empty icon="🎯" msg="No works yet"/>}
            {clientWorks(activeClient.id).map(w=>(
              <div key={w.id} onClick={()=>openWork(w)} style={{background:T.surface,borderRadius:12,padding:bp.isMobile?"12px":"14px 18px",marginBottom:10,cursor:"pointer",border:`1px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:8}}>
                  <div style={{minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                      <SBadge status={w.status}/>
                      <div style={{display:"flex",gap:2}}>{Array.from({length:PRIORITY_STARS[w.priority]||1}).map((_,i)=><span key={i} style={{color:PRIORITY_COLOR[w.priority],fontSize:10}}>★</span>)}</div>
                    </div>
                    <div style={{color:T.text,fontWeight:700,fontSize:bp.isMobile?13:15}}>{w.title}</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:6}}>
                      {w.category&&<span style={{background:T.card,color:T.muted,borderRadius:6,padding:"2px 8px",fontSize:11}}>{w.category}</span>}
                      {w.tags?.slice(0,2).map(t=><span key={t} style={{background:C.accent+"22",color:C.accent,borderRadius:6,padding:"2px 8px",fontSize:11}}>{t}</span>)}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{color:T.text,fontWeight:800,fontSize:14}}>₹{(w.budget||0).toLocaleString()}</div>
                    <div style={{color:w.paid?C.success:C.danger,fontSize:11,fontWeight:700}}>{w.paid?"✓ Paid":"Pending"}</div>
                  </div>
                </div>
                {w.deadline&&<div style={{color:isOverdue(w.deadline,w.status)?C.danger:T.muted,fontSize:11}}>📅 {fmtDate(w.deadline)}{isOverdue(w.deadline,w.status)?" 🚨":""}</div>}
              </div>
            ))}
          </div>
        )}

        {/* WORK DETAIL */}
        {view==="work-detail"&&activeWork&&(
          <WorkDetail work={activeWork} T={T} bp={bp} clients={clients}
            onAdvance={()=>advanceStatus(activeWork)}
            onRevision={note=>addRevision(activeWork,note)}
            onProgress={line=>addProgress(activeWork,line)}
            onDelivery={()=>toggleDelivered(activeWork)}
            onPayment={state=>setPayment(activeWork,state)}
            onNoteChange={notes=>saveNotes(activeWork,notes)}
            onWhatsApp={(type)=>sendWhatsApp(activeWork,type)}
          />
        )}
      </main>

      {/* MOBILE BOTTOM NAV */}
      {bp.isMobile&&(
        <nav style={{position:"fixed",bottom:0,left:0,right:0,background:T.surface,borderTop:`1px solid ${T.border}`,display:"flex",padding:"8px 0 12px",zIndex:100}}>
          {[["dashboard","📊","Dashboard"],["clients","👥","Clients"],["analytics","📈","Analytics"]].map(([k,icon,label])=>(
            <button key={k} onClick={()=>navTo(k)} style={{flex:1,background:"none",border:"none",color:(view===k||view==="client-detail"&&k==="clients"||view==="work-detail"&&k==="clients")?C.accent:T.muted,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"4px 0"}}>
              <span style={{fontSize:20}}>{icon}</span>
              <span style={{fontSize:10,fontWeight:700}}>{label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* PWA INSTALL BANNER */}
      {showInstallBanner && (
        <div style={{position:"fixed",bottom:bp.isMobile?80:24,left:bp.isMobile?12:24,right:bp.isMobile?12:"auto",background:T.surface,border:`1px solid ${C.accent}`,borderRadius:16,padding:"14px 18px",zIndex:150,boxShadow:"0 8px 32px rgba(108,99,255,0.3)",display:"flex",alignItems:"center",gap:14,maxWidth:360}}>
          <div style={{width:44,height:44,background:C.accent,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:T.text,fontWeight:700,fontSize:14}}>Install Workflow</div>
            <div style={{color:T.muted,fontSize:12}}>workflow.loytro.com</div>
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            <button onClick={()=>setShowInstallBanner(false)}
              style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:8,padding:"7px 12px",fontWeight:600,fontSize:12}}>
              Cancel
            </button>
            <button onClick={async()=>{
              if(installPrompt){
                installPrompt.prompt();
                const {outcome}=await installPrompt.userChoice;
                if(outcome==="accepted") notify("App installed! 🎉");
              }
              setShowInstallBanner(false);
            }}
              style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12}}>
              Install
            </button>
          </div>
        </div>
      )}

      {/* QUICK ADD FAB */}
      <button onClick={()=>setShowQuickAdd(true)}
        style={{position:"fixed",bottom:bp.isMobile?80:24,right:20,width:52,height:52,borderRadius:"50%",background:C.accent,color:"#fff",border:"none",fontSize:26,fontWeight:700,boxShadow:"0 4px 20px rgba(108,99,255,0.5)",zIndex:90,display:"flex",alignItems:"center",justifyContent:"center"}}>
        +
      </button>

      {/* MODALS */}
      {modal==="add-client"&&<AddClientModal T={T} bp={bp} onSave={async d=>{await addClient(d);setModal(null);}} onClose={()=>setModal(null)}/>}
      {modal==="add-work"&&activeClient&&<AddWorkModal T={T} bp={bp} clientId={activeClient.id} onSave={async d=>{await addWork(d);setModal(null);}} onClose={()=>setModal(null)}/>}
      {modal==="report"&&activeClient&&<ReportModal T={T} bp={bp} client={activeClient} works={clientWorks(activeClient.id)} onClose={()=>setModal(null)}/>}
      {modal==="user-menu"&&<div style={{position:"fixed",inset:0,zIndex:150}} onClick={()=>setModal(null)}/>}

      {/* QUICK ADD */}
      {showQuickAdd&&(
        <QuickAdd T={T} bp={bp} clients={clients}
          onSave={async d=>{await addWork(d);setShowQuickAdd(false);notify("Quick work added! 🚀");}}
          onClose={()=>setShowQuickAdd(false)}/>
      )}
    </div>
  );
}

// ── Work Detail ───────────────────────────────────────────────────────────────
function WorkDetail({work,T,bp,clients,onAdvance,onRevision,onProgress,onDelivery,onPayment,onNoteChange,onWhatsApp}){
  const [revNote,setRevNote]=useState("");
  const [progLine,setProgLine]=useState("");
  const [noteText,setNoteText]=useState(work.notes||"");
  const noteTimer=useRef(null);
  const idx=STATUS_FLOW.indexOf(work.status);
  const cl=clients.find(c=>c.id===work.client_id);
  const panel={background:T.surface,borderRadius:14,padding:bp.isMobile?"14px":"18px",border:`1px solid ${T.border}`,marginBottom:14};
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"10px 13px",fontSize:13,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};

  return (
    <div>
      <div style={panel}>
        <div style={{color:C.muted,fontSize:12,fontWeight:700,marginBottom:4}}>{cl?.name?.toUpperCase()||"CLIENT"}</div>
        <div style={{color:T.text,fontWeight:800,fontSize:bp.isMobile?18:22,marginBottom:6}}>{work.title}</div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:10}}>
          {work.deadline&&<div style={{color:isOverdue(work.deadline,work.status)?C.danger:T.muted,fontSize:12}}>📅 {fmtDate(work.deadline)}{isOverdue(work.deadline,work.status)?" 🚨":""}</div>}
          {work.category&&<span style={{background:T.card,color:T.muted,borderRadius:6,padding:"2px 8px",fontSize:11}}>{work.category}</span>}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {work.tags?.map(t=><span key={t} style={{background:C.accent+"22",color:C.accent,border:`1px solid ${C.accent}33`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:600}}>{t}</span>)}
          </div>
          {/* WhatsApp Quick Send */}
          <button onClick={()=>onWhatsApp("status")}
            style={{background:"#25D366",color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontWeight:700,fontSize:12,flexShrink:0}}>
            📤 WA Update
          </button>
        </div>
      </div>

      {/* Status */}
      <div style={panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,letterSpacing:"0.06em"}}>CURRENT STATUS</div>
          <div style={{color:STATUS_COLOR[work.status],fontWeight:800,fontSize:13}}>{work.status}</div>
        </div>
        <div style={{position:"relative",marginBottom:16}}>
          <div style={{height:6,background:T.card,borderRadius:4,overflow:"hidden",marginBottom:10}}>
            <div style={{height:"100%",background:STATUS_COLOR[work.status],width:`${(idx/(STATUS_FLOW.length-1))*100}%`,borderRadius:4,transition:"width 0.4s"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            {STATUS_FLOW.map((st,i)=>(
              <div key={st} style={{textAlign:"center",flex:1}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:i<=idx?STATUS_COLOR[work.status]:T.border,margin:"0 auto 4px",border:i===idx?`2px solid ${STATUS_COLOR[work.status]}`:""}}/>
                <div style={{color:i<=idx?T.text:T.muted,fontSize:9,fontWeight:i===idx?700:400,display:bp.isMobile?"none":"block"}}>{st}</div>
              </div>
            ))}
          </div>
          <div style={{color:T.muted,fontSize:11,textAlign:"center",marginTop:6}}>Step {idx+1} of {STATUS_FLOW.length}</div>
        </div>
        {work.status!=="Completed"
          ?<button onClick={onAdvance} style={{width:"100%",background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"13px",fontWeight:700,fontSize:14}}>Advance Status →</button>
          :<div style={{background:C.success+"22",color:C.success,borderRadius:10,padding:"13px",textAlign:"center",fontWeight:700}}>✅ Project Complete!</div>
        }
      </div>

      {/* Delivery + Payment */}
      <div style={panel}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>▶ DELIVERY</div>
            <button onClick={onDelivery} style={{width:"100%",background:work.delivered?"rgba(34,197,94,0.15)":T.card,color:work.delivered?C.success:T.muted,border:`1.5px solid ${work.delivered?C.success:T.border}`,borderRadius:10,padding:"11px 8px",fontWeight:700,fontSize:bp.isMobile?11:12,textAlign:"center"}}>
              {work.delivered?"✓ Delivered":"○ Mark Delivered"}
            </button>
          </div>
          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>⚡ ADVANCE</div>
            <button onClick={()=>onPayment(work.advance_received?"none":"advance")} style={{width:"100%",background:work.advance_received?"rgba(108,99,255,0.2)":T.card,color:work.advance_received?C.accent:T.muted,border:`1.5px solid ${work.advance_received?C.accent:T.border}`,borderRadius:10,padding:"11px 8px",fontWeight:700,fontSize:bp.isMobile?11:12,textAlign:"center"}}>
              {work.advance_received?"✓ Adv. Received":"○ Adv. Received"}
            </button>
          </div>
        </div>
        {!work.paid&&work.advance_received&&(
          <button onClick={()=>onPayment("full")} style={{width:"100%",background:C.success,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,marginBottom:12}}>💰 Mark Full Payment Received</button>
        )}
        {work.paid&&<div style={{background:"rgba(34,197,94,0.15)",color:C.success,borderRadius:10,padding:"10px",textAlign:"center",fontWeight:700,fontSize:13,marginBottom:12}}>✅ Full Payment Received!</div>}

        {/* Financial */}
        <div style={{background:T.bg,borderRadius:10,padding:"12px 14px"}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>FINANCIAL OVERVIEW</div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <div><div style={{color:T.muted,fontSize:11}}>Total Budget</div><div style={{color:T.text,fontWeight:800,fontSize:18}}>₹{(work.budget||0).toLocaleString()}</div></div>
            <div style={{textAlign:"right"}}><div style={{color:T.muted,fontSize:11}}>Remaining</div><div style={{color:work.paid?C.success:C.danger,fontWeight:800,fontSize:18}}>₹{((work.budget||0)-(work.advance||0)).toLocaleString()}</div></div>
          </div>
          <div style={{height:6,background:T.card,borderRadius:4,overflow:"hidden",marginBottom:8}}>
            <div style={{height:"100%",background:work.paid?C.success:C.accent,width:`${work.budget>0?Math.min(100,(work.advance/work.budget)*100):0}%`,borderRadius:4}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <div style={{color:T.muted,fontSize:11}}>{work.budget>0?Math.round((work.advance/work.budget)*100):0}% Advance Paid</div>
            <div style={{color:T.muted,fontSize:11}}>{work.paid?"100":"0"}% Full</div>
          </div>
        </div>
      </div>

      {/* Invoice Generator */}
      <div style={panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,letterSpacing:"0.06em"}}>🧾 INVOICE</div>
        </div>
        <button onClick={()=>{
          const cl2=clients.find(c=>c.id===work.client_id);
          const invoiceNo=`INV-${new Date().getFullYear()}-${String(Math.floor(Math.random()*9000)+1000)}`;
          const win=window.open("","_blank");
          win.document.write(`<!DOCTYPE html><html><head><title>${invoiceNo}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Inter,sans-serif;color:#111;padding:40px;max-width:700px;margin:0 auto}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;padding-bottom:20px;border-bottom:2px solid #6C63FF}.logo{font-size:22px;font-weight:900;color:#6C63FF}.inv-no{text-align:right}.inv-no h2{font-size:20px;font-weight:800}.inv-no p{color:#6b7280;font-size:13px;margin-top:4px}.section{margin-bottom:28px}.label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#6b7280;margin-bottom:8px}.name{font-size:16px;font-weight:700}.sub{font-size:13px;color:#6b7280;margin-top:3px}table{width:100%;border-collapse:collapse;margin:20px 0}th{background:#f4f5fb;padding:10px 14px;text-align:left;font-size:12px;font-weight:700;color:#6b7280;text-transform:uppercase}td{padding:12px 14px;border-bottom:1px solid #e5e7eb;font-size:14px}.total-row{background:#f4f5fb;font-weight:800}.amount{text-align:right}.status{display:inline-block;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:700}.paid{background:#22C55E22;color:#22C55E}.pending{background:#EF444422;color:#EF4444}.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:12px;text-align:center}</style></head><body>
          <div class="header"><div class="logo">◈ Workflow</div><div class="inv-no"><h2>${invoiceNo}</h2><p>Date: ${new Date().toLocaleDateString("en-IN")}</p><span class="status ${work.paid?"paid":"pending"}">${work.paid?"PAID":"PENDING"}</span></div></div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-bottom:32px">
          <div class="section"><div class="label">From</div><div class="name">Your Business</div><div class="sub">workflow.loytro.com</div></div>
          <div class="section"><div class="label">Bill To</div><div class="name">${cl2?.name||"Client"}</div>${cl2?.company?`<div class="sub">${cl2.company}</div>`:""}${cl2?.phone?`<div class="sub">${cl2.phone}</div>`:""}</div></div>
          <table><tr><th>Description</th><th>Category</th><th class="amount">Amount</th></tr>
          <tr><td><b>${work.title}</b>${work.description?`<br><span style="font-size:12px;color:#6b7280">${work.description}</span>`:""}</td><td>${work.category||"—"}</td><td class="amount">₹${(work.budget||0).toLocaleString()}</td></tr>
          <tr class="total-row"><td colspan="2"><b>Total</b></td><td class="amount"><b>₹${(work.budget||0).toLocaleString()}</b></td></tr>
          <tr><td colspan="2" style="color:#6b7280">Advance Received</td><td class="amount" style="color:#22C55E">₹${(work.advance||0).toLocaleString()}</td></tr>
          <tr class="total-row"><td colspan="2"><b>Balance Due</b></td><td class="amount" style="color:${work.paid?"#22C55E":"#EF4444"}"><b>₹${work.paid?0:((work.budget||0)-(work.advance||0)).toLocaleString()}</b></td></tr></table>
          ${work.revisions.length?`<div style="background:#f4f5fb;padding:14px;border-radius:8px;margin:20px 0"><div class="label">Revisions Included</div>${work.revisions.map((r,i)=>`<div style="font-size:13px;padding:4px 0">${i+1}. ${r.note}</div>`).join("")}</div>`:""}
          <div class="footer">Generated by ◈ Workflow · ${new Date().toLocaleString("en-IN")} · workflow.loytro.com</div>
          </body></html>`);
          win.document.close(); win.print();
        }} style={{width:"100%",background:T.card,color:T.text,border:`1px solid ${T.border}`,borderRadius:10,padding:"12px",fontWeight:700,fontSize:13}}>
          🧾 Generate Invoice PDF
        </button>
      </div>

      {/* Revisions */}
      <div style={panel}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{color:T.muted,fontSize:11,fontWeight:700,letterSpacing:"0.06em"}}>REVISIONS ({work.revisions.length})</div>
        </div>
        {work.revisions.map((r,i)=>(
          <div key={r.id} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
            <div style={{width:20,height:20,borderRadius:"50%",background:C.accent+"22",color:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,flexShrink:0}}>{i+1}</div>
            <div style={{flex:1,color:T.text,fontSize:13}}>{r.note}</div>
            <div style={{color:T.muted,fontSize:10,flexShrink:0}}>{fmt(r.created_at)}</div>
          </div>
        ))}
        {work.revisions.length===0&&<div style={{color:T.muted,fontSize:13,textAlign:"center",padding:"10px 0"}}>No revisions yet</div>}
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <input style={{...inp,flex:1}} placeholder="Log revision request…" value={revNote}
            onChange={e=>setRevNote(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&revNote.trim()){onRevision(revNote.trim());setRevNote("");}}}/>
          <button onClick={()=>{if(revNote.trim()){onRevision(revNote.trim());setRevNote("");}}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontWeight:700,fontSize:14}}>+</button>
        </div>
        {work.revisions.length>0&&(
          <button onClick={()=>onWhatsApp("revision")} style={{marginTop:10,width:"100%",background:"#25D366",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontWeight:700,fontSize:12}}>
            📤 Send Revision Update on WhatsApp
          </button>
        )}
      </div>

      {/* Progress + Notes */}
      <div style={panel}>
        <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:10,letterSpacing:"0.06em"}}>WORKSPACE NOTES <span style={{color:T.muted,fontSize:10,fontWeight:400}}>🔄 Auto-saving</span></div>
        <textarea style={{...inp,height:80,resize:"none",marginBottom:12}} value={noteText}
          onChange={e=>{setNoteText(e.target.value);clearTimeout(noteTimer.current);noteTimer.current=setTimeout(()=>onNoteChange(e.target.value),800);}}
          placeholder="Jot down ideas, meeting notes…"/>
        <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>DAILY PROGRESS</div>
        {work.progress.slice(-4).map((p,i)=>(
          <div key={p.id||i} style={{display:"flex",gap:8,padding:"5px 0",borderBottom:`1px solid ${T.border}`,alignItems:"flex-start"}}>
            <span style={{color:C.accent,fontSize:10,flexShrink:0,paddingTop:2}}>{fmt(p.created_at)}</span>
            <span style={{color:T.text,fontSize:12}}>· {p.line}</span>
          </div>
        ))}
        <div style={{display:"flex",gap:8,marginTop:10}}>
          <input style={{...inp,flex:1}} placeholder="Today's update…" value={progLine}
            onChange={e=>setProgLine(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&progLine.trim()){onProgress(progLine.trim());setProgLine("");}}}/>
          <button onClick={()=>{if(progLine.trim()){onProgress(progLine.trim());setProgLine("");}}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontWeight:700,fontSize:13}}>Save</button>
        </div>
      </div>

      {/* Timeline */}
      <div style={panel}>
        <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:14,letterSpacing:"0.06em"}}>PROJECT HISTORY</div>
        <div style={{position:"relative",paddingLeft:24}}>
          <div style={{position:"absolute",left:8,top:6,bottom:6,width:2,background:T.border,borderRadius:2}}/>
          {[...work.timeline].reverse().map((t,i)=>(
            <div key={i} style={{position:"relative",paddingBottom:12}}>
              <div style={{position:"absolute",left:-24,top:2,width:10,height:10,borderRadius:"50%",background:i===0?C.accent:T.border,border:i===0?`2px solid ${C.accent}`:""}}/>
              <div style={{color:T.text,fontSize:13,fontWeight:600}}>{t.event}</div>
              <div style={{color:T.muted,fontSize:11,marginTop:2}}>{fmt(t.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Quick Add ─────────────────────────────────────────────────────────────────
function QuickAdd({T,bp,clients,onSave,onClose}){
  const [title,setTitle]=useState("");
  const [clientId,setClientId]=useState(clients[0]?.id||"");
  const [category,setCategory]=useState("Reel");
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"12px 14px",fontSize:14,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:bp.isMobile?"flex-end":"center",justifyContent:"center",zIndex:200,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:bp.isMobile?"18px 18px 0 0":"18px",padding:"20px",width:"100%",maxWidth:400,border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>⚡ Quick Add Work</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <input style={inp} placeholder="Work title *" value={title} onChange={e=>setTitle(e.target.value)} autoFocus/>
          <select style={{...inp,cursor:"pointer"}} value={clientId} onChange={e=>setClientId(e.target.value)}>
            {clients.length===0&&<option value="">No clients — add one first</option>}
            {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={{...inp,cursor:"pointer"}} value={category} onChange={e=>setCategory(e.target.value)}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:16}}>
          <button onClick={()=>{if(title.trim()&&clientId)onSave({client_id:clientId,title:title.trim(),category,priority:"Medium",tags:[],budget:0,advance:0});}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:14}}>Create →</button>
          <button onClick={onClose} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"13px",fontWeight:600,fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Report Modal ──────────────────────────────────────────────────────────────
function ReportModal({T,bp,client,works,onClose}){
  const total=works.reduce((a,w)=>a+(w.budget||0),0);
  const received=works.reduce((a,w)=>a+(w.paid?(w.budget||0):w.advance_received?(w.advance||0):0),0);
  const printReport=()=>{
    const win=window.open("","_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>Report-${client.name}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Inter,sans-serif;padding:32px;color:#111;max-width:800px;margin:0 auto}h1{color:#6C63FF;margin-bottom:4px}.boxes{display:flex;gap:12px;margin:20px 0}.box{background:#f4f5fb;padding:14px 20px;border-radius:10px;min-width:140px}.box b{display:block;font-size:20px;margin-bottom:4px}.box span{font-size:12px;color:#6b7280}table{width:100%;border-collapse:collapse;margin:20px 0}th{background:#f4f5fb;padding:10px 14px;text-align:left;font-size:12px;font-weight:700;color:#6b7280;text-transform:uppercase}td{padding:10px 14px;border-bottom:1px solid #e5e7eb;font-size:13px}.badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700}.paid{background:#22C55E22;color:#22C55E}.pending{background:#EF444422;color:#EF4444}.advance{background:#F59E0B22;color:#F59E0B}footer{margin-top:32px;color:#9ca3af;font-size:12px;text-align:center;border-top:1px solid #e5e7eb;padding-top:16px}</style></head><body>
    <h1>◈ Workflow — Client Report</h1>
    <h2 style="font-size:20px;margin-top:8px">${client.name}${client.company?` <span style="color:#6b7280;font-weight:400">· ${client.company}</span>`:""}</h2>
    ${client.phone?`<p style="color:#6b7280;font-size:13px;margin-top:4px">📱 ${client.phone}</p>`:""}
    <div class="boxes">
      <div class="box"><b style="color:#6C63FF">₹${total.toLocaleString()}</b><span>Total Budget</span></div>
      <div class="box"><b style="color:#22C55E">₹${received.toLocaleString()}</b><span>Received</span></div>
      <div class="box"><b style="color:#EF4444">₹${(total-received).toLocaleString()}</b><span>Pending</span></div>
    </div>
    <table><tr><th>Work</th><th>Category</th><th>Status</th><th>Revisions</th><th>Delivered</th><th>Payment</th></tr>
    ${works.map(w=>`<tr><td><b>${w.title}</b></td><td>${w.category||"—"}</td><td>${w.status}</td><td>${w.revisions.length}</td><td>${w.delivered?"✓":"No"}</td><td><span class="${w.paid?"paid":w.advance_received?"advance":"pending"}">${w.paid?"Full Paid":w.advance_received?"Advance":"Pending"}</span></td></tr>`).join("")}
    </table>
    <footer>Generated by ◈ Workflow · ${new Date().toLocaleString("en-IN")}</footer>
    </body></html>`);
    win.document.close(); win.print();
  };
  const whatsapp=()=>{
    const msg=`*◈ Workflow Report*\n*Client:* ${client.name}\n*Total Works:* ${works.length}\n*Budget:* ₹${total.toLocaleString()}\n*Received:* ₹${received.toLocaleString()}\n*Pending:* ₹${(total-received).toLocaleString()}\n\n${works.map(w=>`• ${w.title} — ${w.status} ${w.paid?"✓ Paid":""}`).join("\n")}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,"_blank");
  };
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:18,padding:bp.isMobile?"16px":"24px",width:"100%",maxWidth:500,maxHeight:"90vh",overflowY:"auto",border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>📄 Report — {client.name}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
          {[["Budget",total,C.accent],["Received",received,C.success],["Pending",total-received,C.danger]].map(([l,v,c])=>(
            <div key={l} style={{background:T.card,borderRadius:10,padding:"14px 10px",textAlign:"center",border:`1px solid ${T.border}`}}>
              <div style={{color:c,fontWeight:900,fontSize:18}}>₹{v.toLocaleString()}</div>
              <div style={{color:T.muted,fontSize:11,marginTop:3}}>{l}</div>
            </div>
          ))}
        </div>
        {works.map(w=>(
          <div key={w.id} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"9px 0",borderBottom:`1px solid ${T.border}`,gap:8}}>
            <div style={{minWidth:0}}>
              <div style={{color:T.text,fontWeight:600,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{w.title}</div>
              <div style={{color:T.muted,fontSize:11}}>{w.revisions.length} rev · {w.delivered?"Delivered":"Pending"}</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <SBadge status={w.status}/>
              <div style={{color:w.paid?C.success:w.advance_received?C.warning:C.danger,fontSize:11,marginTop:4,fontWeight:700}}>{w.paid?"Paid":w.advance_received?"Advance":"Pending"}</div>
            </div>
          </div>
        ))}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:16}}>
          <button onClick={whatsapp} style={{background:"#25D366",color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:13}}>📤 WhatsApp</button>
          <button onClick={printReport} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:13}}>🖨️ Print PDF</button>
        </div>
        <button onClick={onClose} style={{width:"100%",background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"11px",fontWeight:600,fontSize:13,marginTop:10}}>Close</button>
      </div>
    </div>
  );
}

// ── Add Client ────────────────────────────────────────────────────────────────
function AddClientModal({T,bp,onSave,onClose}){
  const [f,setF]=useState({name:"",phone:"",email:"",company:"",notes:""});
  const set=k=>e=>setF(p=>({...p,[k]:e.target.value}));
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"12px 14px",fontSize:14,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:18,padding:bp.isMobile?"16px":"24px",width:"100%",maxWidth:420,border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>👤 Add New Client</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <input style={inp} placeholder="Client Name *" value={f.name} onChange={set("name")}/>
          <input style={inp} placeholder="Phone Number" value={f.phone} onChange={set("phone")} type="tel"/>
          <input style={inp} placeholder="Email Address" value={f.email} onChange={set("email")} type="email"/>
          <input style={inp} placeholder="Company / Organization" value={f.company} onChange={set("company")}/>
          <textarea style={{...inp,height:70,resize:"none"}} placeholder="Notes" value={f.notes} onChange={set("notes")}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:16}}>
          <button onClick={()=>{if(f.name.trim())onSave(f);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:14}}>Save Client</button>
          <button onClick={onClose} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"13px",fontWeight:600,fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Add Work Modal ────────────────────────────────────────────────────────────
function AddWorkModal({T,bp,clientId,onSave,onClose}){
  const [f,setF]=useState({client_id:clientId,title:"",category:"Reel",description:"",deadline:"",priority:"Medium",tags:[],budget:"",advance:""});
  const set=k=>e=>setF(p=>({...p,[k]:e.target.value}));
  const toggleTag=t=>setF(p=>({...p,tags:p.tags.includes(t)?p.tags.filter(x=>x!==t):[...p.tags,t]}));
  const inp={background:T.card,border:`1px solid ${T.border}`,borderRadius:10,color:T.text,padding:"12px 14px",fontSize:13,outline:"none",width:"100%",boxSizing:"border-box",fontFamily:"inherit"};
  return (
    <div style={{position:"fixed",inset:0,background:"#00000095",display:"flex",alignItems:bp.isMobile?"flex-end":"center",justifyContent:"center",zIndex:200,backdropFilter:"blur(6px)"}}>
      <div style={{background:T.surface,borderRadius:bp.isMobile?"18px 18px 0 0":"18px",padding:bp.isMobile?"18px 16px 24px":"24px",width:"100%",maxWidth:480,maxHeight:bp.isMobile?"92vh":"88vh",overflowY:"auto",border:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
          <div style={{color:T.text,fontWeight:800,fontSize:16}}>🎯 New Work Item</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,fontSize:22}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <input style={inp} placeholder="Work Title *" value={f.title} onChange={set("title")}/>
          <select style={{...inp,cursor:"pointer"}} value={f.category} onChange={set("category")}>{CATEGORIES.map(c=><option key={c}>{c}</option>)}</select>
          <textarea style={{...inp,height:60,resize:"none"}} placeholder="Description" value={f.description} onChange={set("description")}/>
          <input style={inp} type="date" value={f.deadline} onChange={set("deadline")}/>
          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>PRIORITY</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {PRIORITIES.map((p,i)=>(
                <button key={p} onClick={()=>setF(prev=>({...prev,priority:p}))}
                  style={{borderRadius:10,padding:"10px 4px",fontWeight:700,fontSize:12,textAlign:"center",background:f.priority===p?PRIORITY_COLOR[p]+"22":T.card,color:f.priority===p?PRIORITY_COLOR[p]:T.muted,border:`1.5px solid ${f.priority===p?PRIORITY_COLOR[p]:T.border}`}}>
                  {"★".repeat(i+1)}<br/>{p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{color:T.muted,fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:"0.06em"}}>TAGS</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {TAGS.map(t=>(
                <button key={t} onClick={()=>toggleTag(t)}
                  style={{background:f.tags.includes(t)?C.accent:T.card,color:f.tags.includes(t)?"#fff":T.muted,border:`1px solid ${f.tags.includes(t)?C.accent:T.border}`,borderRadius:20,padding:"6px 12px",fontSize:11,fontWeight:600}}>
                  {t}{f.tags.includes(t)?" ✕":" +"}
                </button>
              ))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <input style={inp} type="number" placeholder="Budget ₹" value={f.budget} onChange={set("budget")}/>
            <input style={inp} type="number" placeholder="Advance ₹" value={f.advance} onChange={set("advance")}/>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:16}}>
          <button onClick={()=>{if(f.title.trim())onSave(f);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontWeight:700,fontSize:14}}>Create Work</button>
          <button onClick={onClose} style={{background:"none",border:`1px solid ${T.border}`,color:T.muted,borderRadius:12,padding:"13px",fontWeight:600,fontSize:14}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────────
const Av=({name,size=44})=>{
  const colors=[C.accent,"#22C55E","#F59E0B","#EF4444","#38BDF8","#A78BFA"];
  const color=colors[(name?.charCodeAt(0)||0)%colors.length];
  return <div style={{width:size,height:size,borderRadius:"50%",background:color+"33",color,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:size*0.38,flexShrink:0,border:`1.5px solid ${color}44`}}>{name?.[0]?.toUpperCase()||"?"}</div>;
};
const StatusDot=({status})=><div style={{width:10,height:10,borderRadius:"50%",background:STATUS_COLOR[status]||"#6B7280",flexShrink:0}}/>;
const SBadge=({status})=>{const c=STATUS_COLOR[status]||"#6B7280";return <span style={{background:c+"22",color:c,border:`1px solid ${c}44`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{status}</span>;};
const Empty=({icon,msg})=><div style={{textAlign:"center",padding:"28px 16px",color:"#6B7280"}}><div style={{fontSize:32,marginBottom:8}}>{icon}</div><div style={{fontSize:13}}>{msg}</div></div>;
const Toast=({msg,type})=><div style={{position:"fixed",bottom:80,left:"50%",transform:"translateX(-50%)",background:type==="success"?C.success:C.danger,color:"#fff",padding:"12px 22px",borderRadius:12,fontWeight:700,fontSize:13,zIndex:9999,boxShadow:"0 4px 24px #0008",whiteSpace:"nowrap"}}>{type==="success"?"✓":"✕"} {msg}</div>;
const Loader=()=>(
  <div style={{minHeight:"100vh",background:C.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,fontFamily:"'Inter',system-ui,sans-serif"}}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{width:32,height:32,background:C.accent,borderRadius:8,animation:"spin 2s linear infinite",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </div>
    <div style={{color:C.muted,fontSize:14,fontWeight:600}}>Loading Workflow…</div>
  </div>
);
